import { useState } from 'react';
import  {
         Backdrop,    
         Button,
         Box,
         Chip,
         CircularProgress,
         Dialog,
         DialogActions,
         DialogTitle,
         DialogContent,
         Divider,
         IconButton,
         Typography,
         Stack
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { Transition, calculateRoundedTax } from './utils';

export default function OrderDialog({
    company,
    seat,
    order,
    setPayment,
    isOpen,
    openDialog,
    closeDialog
}) {
    const {t} = useTranslation();
    const [isLoading, setIsLoading] = useState(false);
    const cityTax = calculateRoundedTax(order, company.cityTax, false);
    const stateTax = calculateRoundedTax(order, company.stateTax, false);
    const reducedCityTax = calculateRoundedTax(order, company.reducedCityTax, true);
    const reducedStateTax = calculateRoundedTax(order, company.reducedStateTax, true);
    const subtotal = order.reduce((sum, item) => sum + item.total, 0);
    const total = subtotal + cityTax + stateTax + reducedCityTax + reducedStateTax;

    const handleContinue = async () => {
        setIsLoading(true);
        setPayment({
                    subtotal: subtotal,
                    cityTax: cityTax + reducedCityTax,
                    stateTax: stateTax + reducedStateTax,
                    total: total
        });
        closeDialog('orderConfirmation');
        openDialog('tipSelection');
    };

    return (
        <Dialog
            open={isOpen('orderConfirmation')}
            onClose={() => closeDialog('orderConfirmation')}
            keepMounted
            fullScreen
            slots={{transition: Transition}}
            slotProps={{
                paper: {
                    sx: {
                        position: 'relative',
                        maxHeight: '100vh',
                    }
                },
            }}>
        {isLoading && (
            <Backdrop
                sx={(theme) => ({ color: '#FFF', zIndex: theme.zIndex.drawer + 1 })}
                open={isLoading}>
                <CircularProgress color="inherit" />
            </Backdrop>
        )}
        <DialogTitle>
            <Box display="flex" alignItems="center">
                <IconButton onClick={() => closeDialog('orderConfirmation')} edge="start" sx={{ mr: 1 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h6">{t('orderReview')}</Typography>
            </Box>
        </DialogTitle>
        <DialogContent sx={{p: 2}}>
            <Stack spacing={1}>
                {order.map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"}}>
                        <Box sx={{width: '100%'}}>
                            <Box sx={{display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between', gap: 1}}>
                                <Typography>{item.name} </Typography>
                                <Chip label={item.quantity && `x${item.quantity}`} />
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                            {item.modifiers.map((modifier) => modifier.name).join(', ')}
                            </Typography>
                        </Box>
                        <Box sx={{ minWidth: 80, textAlign: 'right' }}>
                            <Typography >
                                ${item.total.toFixed(2)}
                            </Typography>
                        </Box>
                    </Box>
                ))}

                <Box>
                    <Box display="flex" justifyContent="space-between">
                        <Typography fontWeight="bold">Subtotal</Typography>
                        <Typography fontWeight="bold">${subtotal.toFixed(2)}</Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />
                </Box>

                {(cityTax > 0 || stateTax > 0 || reducedCityTax > 0 || reducedStateTax > 0) && (
                <Box>
                    <Typography fontWeight="bold" sx={{ mb: 1 }}>{t('taxesLabel') || 'Taxes'}</Typography>

                    {[
                    { label: t('cityTaxLabel'), rate: company.cityTax, amount: cityTax },
                    { label: t('stateTaxLabel'), rate: company.stateTax, amount: stateTax },
                    { label: t('reducedCityTaxLabel'), rate: company.reducedCityTax, amount: reducedCityTax },
                    { label: t('reducedStateTaxLabel'), rate: company.reducedStateTax, amount: reducedStateTax },
                    ].map(({ label, rate, amount }, index) =>
                    amount > 0 ? (
                        <Box key={index} display="flex" justifyContent="space-between">
                            <Typography>{label} ({rate.toFixed(2)}%)</Typography>
                            <Typography>${amount.toFixed(2)}</Typography>
                        </Box>
                    ) : null
                    )}

                    <Divider sx={{ my: 2 }} />
                </Box>
                )}

                <Box display="flex" justifyContent="space-between">
                    <Typography fontWeight="bold">Total</Typography>
                    <Typography fontWeight="bold">${total.toFixed(2)}</Typography>
                </Box>
            </Stack>
        </DialogContent>
        <DialogActions> 
            <Button
                disabled={isLoading}
                variant="contained"
                color="primary"
                size="large"
                startIcon={<ReceiptLongIcon />}
                onClick={handleContinue}>
                {t('orderPayButton')}
            </Button>
        </DialogActions>
    </Dialog>
    )
}
